import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from './table.js';
import { Button } from './button.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger, } from './dropdown-menu.js';
import { Edit, Trash2, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { SpinnerLoader } from './spinner-loader.js';
export var DataTable = function (_a) {
    var data = _a.data, columns = _a.columns, _b = _a.actions, actions = _b === void 0 ? [] : _b, pagination = _a.pagination, _c = _a.loading, loading = _c === void 0 ? false : _c, _d = _a.emptyMessage, emptyMessage = _d === void 0 ? 'No data available' : _d, _e = _a.className, className = _e === void 0 ? '' : _e, onSort = _a.onSort, sortConfig = _a.sortConfig, _f = _a.showActions, showActions = _f === void 0 ? true : _f, _g = _a.actionsLabel, actionsLabel = _g === void 0 ? 'Actions' : _g, _h = _a.stickyHeader, stickyHeader = _h === void 0 ? false : _h, _j = _a.stickyFooter, stickyFooter = _j === void 0 ? false : _j, onRowClick = _a.onRowClick, _k = _a.rowClickable, rowClickable = _k === void 0 ? false : _k, _l = _a.maxHeight, maxHeight = _l === void 0 ? '600px' : _l;
    var handleSort = function (key) {
        if (!onSort)
            return;
        var newDirection = (sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.key) === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        onSort(key, newDirection);
    };
    var getCellValue = function (row, column) {
        if (typeof column.accessor === 'function') {
            return column.accessor(row);
        }
        return row[column.accessor];
    };
    var renderCell = function (row, column, index) {
        var value = getCellValue(row, column);
        if (column.render) {
            return column.render(value, row, index);
        }
        return value;
    };
    var renderActions = function (row, index) {
        if (!showActions || actions.length === 0)
            return null;
        return (_jsx("div", { className: "flex items-center justify-start space-x-1 -ml-1", children: actions.map(function (action, actionIndex) {
                var _a;
                return (_jsx(Button, { variant: action.variant || 'ghost', size: "sm", className: "px-1 py-1 h-auto", onClick: function (e) {
                        e.stopPropagation(); // Prevent row click when clicking action
                        action.onClick(row, index);
                    }, disabled: (_a = action.disabled) === null || _a === void 0 ? void 0 : _a.call(action, row), "aria-label": action.label, children: action.icon }, actionIndex));
            }) }));
    };
    var handleRowClick = function (row, index) {
        if (rowClickable && onRowClick) {
            onRowClick(row, index);
        }
    };
    // Helper function to get alignment classes
    var getAlignmentClasses = function (align) {
        switch (align) {
            case 'center':
                return 'text-center justify-center';
            case 'right':
                return 'text-right justify-end';
            default:
                return 'text-left justify-start';
        }
    };
    var renderPagination = function () {
        if (!pagination)
            return null;
        var currentPage = pagination.currentPage, totalPages = pagination.totalPages, pageSize = pagination.pageSize, totalItems = pagination.totalItems, onPageChange = pagination.onPageChange, onPageSizeChange = pagination.onPageSizeChange, _a = pagination.pageSizeOptions, pageSizeOptions = _a === void 0 ? [15, 25, 50, 100] : _a;
        var startItem = (currentPage - 1) * pageSize + 1;
        var endItem = Math.min(currentPage * pageSize, totalItems);
        return (_jsxs("div", { className: "flex items-center justify-between px-2 py-4 bg-background border-t w-full ".concat(stickyFooter ? 'sticky bottom-0 z-10' : ''), children: [_jsxs("div", { className: "flex items-center space-x-4", children: [_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("span", { className: "text-sm text-muted-foreground", children: "Items per page" }), _jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsxs(Button, { variant: "outline", size: "sm", className: "h-8 w-16 justify-between px-2 font-normal bg-card text-foreground border-input hover:bg-muted", "aria-label": "Items per page", children: [pageSize, _jsx(ChevronDown, { className: "h-4 w-4 text-muted-foreground opacity-70" })] }) }), _jsx(DropdownMenuContent, { align: "start", className: "min-w-[4rem] bg-popover text-popover-foreground border-border", children: _jsx(DropdownMenuRadioGroup, { value: String(pageSize), onValueChange: function (value) { return onPageSizeChange(Number(value)); }, children: pageSizeOptions.map(function (size) { return (_jsx(DropdownMenuRadioItem, { value: String(size), className: "cursor-pointer", children: size }, size)); }) }) })] })] }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: function () { return onPageChange(currentPage - 1); }, disabled: currentPage === 1, className: "h-8 w-8 p-0 hover:bg-muted disabled:opacity-50 bg-transparent", "aria-label": "Previous page", children: _jsx(ChevronLeft, { className: "h-4 w-4" }) }), _jsx(Button, { variant: "ghost", size: "sm", onClick: function () { return onPageChange(currentPage + 1); }, disabled: currentPage === totalPages, className: "h-8 w-8 p-0 hover:bg-muted disabled:opacity-50 bg-transparent", "aria-label": "Next page", children: _jsx(ChevronRight, { className: "h-4 w-4" }) })] }), _jsxs("span", { className: "text-sm text-muted-foreground", children: [startItem, " - ", endItem, " of ", totalItems.toLocaleString()] })] }), _jsx("div", {})] }));
    };
    // When maxHeight is 100%, fill the parent via flex instead of fixed min/max heights
    var fillContainer = maxHeight === '100%';
    if (loading) {
        return (_jsx("div", { className: "flex items-center justify-center p-8 ".concat(fillContainer ? 'h-full min-h-0' : '', " ").concat(className), children: _jsx(SpinnerLoader, { size: 2 }) }));
    }
    return (_jsxs("div", { className: "w-full flex flex-col min-h-0 ".concat(className), children: [_jsx("div", { className: "overflow-auto relative flex-1 min-h-0", style: fillContainer
                    ? undefined
                    : {
                        maxHeight: pagination ? "calc(".concat(maxHeight, " - 70px)") : maxHeight,
                        minHeight: pagination ? "calc(".concat(maxHeight, " - 70px)") : maxHeight,
                    }, children: _jsxs(Table, { children: [_jsx(TableHeader, { className: stickyHeader ? 'sticky top-[-1px] bg-background z-10' : '', children: _jsxs(TableRow, { children: [columns.map(function (column) { return (_jsx(TableHead, { className: "".concat(getAlignmentClasses(column.align), " ").concat(column.sortable ? 'cursor-pointer hover:bg-secondary/50 dark:hover:bg-muted/50' : ''), style: { width: column.width }, onClick: function () { return column.sortable && handleSort(column.key); }, children: _jsxs("div", { className: "flex items-center space-x-1 ".concat(getAlignmentClasses(column.align)), children: [typeof column.header === 'string' ? (_jsx("span", { children: column.header })) : (column.header), column.sortable && (sortConfig === null || sortConfig === void 0 ? void 0 : sortConfig.key) === column.key && (_jsx("span", { className: "text-xs", children: sortConfig.direction === 'asc' ? '↑' : '↓' }))] }) }, column.key)); }), showActions && actions.length > 0 && (_jsx(TableHead, { className: "text-left", children: actionsLabel }))] }) }), _jsx(TableBody, { children: data.length === 0 ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: columns.length + (showActions && actions.length > 0 ? 1 : 0), className: "h-24 text-center text-muted-foreground", children: emptyMessage }) })) : (data.map(function (row, index) { return (_jsxs(TableRow, { className: "hover:bg-secondary/50 dark:hover:bg-muted/50 ".concat(rowClickable ? 'cursor-pointer' : ''), onClick: function () { return handleRowClick(row, index); }, children: [columns.map(function (column) { return (_jsx(TableCell, { className: getAlignmentClasses(column.align), children: renderCell(row, column, index) }, column.key)); }), showActions && actions.length > 0 && (_jsx(TableCell, { className: "text-left pl-2", children: renderActions(row, index) }))] }, index)); })) })] }) }), pagination && (_jsx("div", { className: "w-full flex-shrink-0", children: renderPagination() }))] }));
};
// Example usage and stories data
export var sampleData = [
    {
        id: 1,
        name: 'Engage & Inspire: Sales Outreach Template',
        createdAt: '02/20/2023',
        status: 'active',
        type: 'template',
    },
    {
        id: 2,
        name: 'Targeted Engagement: PrizePicks Tier-B Outreach',
        createdAt: '06/30/2023',
        status: 'active',
        type: 'campaign',
    },
    {
        id: 3,
        name: 'State-Specific Strategies: PrizePicks Tier-A Outreach',
        createdAt: '03/05/2023',
        status: 'inactive',
        type: 'strategy',
    },
];
export var sampleColumns = [
    {
        key: 'name',
        header: 'Name',
        accessor: 'name',
        sortable: true,
        width: '40%',
    },
    {
        key: 'createdAt',
        header: 'Created At',
        accessor: 'createdAt',
        sortable: true,
        align: 'center',
        width: '20%',
    },
    {
        key: 'status',
        header: 'Status',
        accessor: 'status',
        width: '15%',
        render: function (value) { return (_jsx("span", { className: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ".concat(value === 'active'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 dark:bg-muted text-gray-800 dark:text-foreground'), children: value })); },
    },
    {
        key: 'type',
        header: 'Type',
        accessor: 'type',
        sortable: true,
        width: '15%',
    },
];
export var sampleActions = [
    {
        label: 'Edit',
        icon: _jsx(Edit, { className: "h-4 w-4" }), onClick: function (row) { return console.log('Edit:', row); },
        variant: 'ghost',
    },
    {
        label: 'Delete',
        icon: _jsx(Trash2, { className: "h-4 w-4" }),
        onClick: function (row) { return console.log('Delete:', row); },
        variant: 'ghost',
    },
];
export default DataTable;
